import API from "@/api";

import moment from "@/utils/moment";

import orderMixin from "@/mixins/orderMixin";

const RESET = "RESET";
const SET_PRELOADER = "SET_PRELOADER";
const SET_ORDER = "SET_ORDER";
const INITIALIZE_STORE = "INITIALIZE_STORE";
const SET_CANCELLATION_ACTION = "SET_CANCELLATION_ACTION";
const STEP = "STEP";
const SET_CANCELLATION_REASON = "SET_CANCELLATION_REASON";
const UPDATE_REFUND_INFO = "UPDATE_REFUND_INFO";
const UPDATE_RESCHEDULE_INFO = "UPDATE_RESCHEDULE_INFO";
const SET_TICKET_CREATED = "SET_TICKET_CREATED";
const SET_TICKET_CREATION_ERROR = "SET_TICKET_CREATION_ERROR";

const defaultState = () => {
  return {
    preloader: false,
    ticketCreated: false,
    ticketCreationError: false,
    exitAction: null,
    order: null,
    cancellationReason: null,
    cancellationAction: null,
    step: 0,
    reason: "",
    refund: {
      bank: null,
      agency: null,
      account: null,
      accountType: null
    },
    reschedule: {
      date: null,
      initialHour: null,
      type: "ANY"
    }
  };
};

const actions = {
  async reset({ commit }) {
    commit(RESET);
  },

  setOrderObject({ commit }, order) {
    commit(SET_ORDER, order);
  },

  initializeStore({ commit }, payload) {
    commit(INITIALIZE_STORE, payload);
  },

  setCancellationAction({ commit, dispatch }, action) {
    commit(SET_CANCELLATION_ACTION, action);
    dispatch("nextStep");
  },

  nextStep({ state, commit, getters, dispatch }) {
    if (state.ticketCreated || state.ticketCreationError) {
      state.exitAction();
      return;
    }

    if (getters.canFinishCancellation && getters.allowToSendTicket) {
      dispatch("createCancellationTicket");

      return;
    }

    if (state.step < 2) {
      commit(STEP, state.step + 1);
    }
  },

  previousStep({ state, commit }) {
    if (
      state.step === 0 ||
      (state.step === 2 && state.cancellationReason === "rescheduling")
    ) {
      state.exitAction();

      return;
    }

    if (state.step > 0) {
      commit(STEP, state.step - 1);
    }
  },

  setCancellationReason({ commit }, reason) {
    commit(SET_CANCELLATION_REASON, reason);
  },

  updateRefund({ commit }, payload) {
    commit(UPDATE_REFUND_INFO, payload);
  },

  updateReschedule({ commit }, payload) {
    commit(UPDATE_RESCHEDULE_INFO, payload);
  },

  async createCancellationTicket({ commit, state }) {
    commit(SET_PRELOADER, true);
    try {
      if (["cancel", "credit"].includes(state.cancellationAction)) {
        await API.Support.createCancellationTicket(
          state.order.id,
          state.reason,
          state.cancellationAction === "cancel" ? state.refund : null
        );
      }

      if (state.cancellationAction === "reschedule") {
        await API.Support.createRescheduleTicket(state.order.id, state.reason, {
          ...state.reschedule,
          date: state.reschedule.date.toLocaleDateString("pt-BR")
        });
      }

      if (state.cancellationAction === "inform-absence") {
        await API.Support.createNoShowInOrderTicket();
      }

      commit(SET_TICKET_CREATED);
    } catch (e) {
      commit(SET_TICKET_CREATION_ERROR);
    }
    commit(SET_PRELOADER, false);
  }
};

const mutations = {
  [RESET](state) {
    Object.assign(state, defaultState());
  },

  [SET_PRELOADER](state, loading) {
    state.preloader = loading;
  },

  [SET_ORDER](state, order) {
    state.order = order;
  },

  [INITIALIZE_STORE](state, { order, reason, exit }) {
    state.cancellationReason = reason;
    state.order = order;
    state.exitAction = exit;

    if (reason === "rescheduling") {
      state.cancellationAction = "reschedule";
      state.step = 2;

      return;
    }
  },

  [SET_CANCELLATION_ACTION](state, reason) {
    state.cancellationAction = reason;
  },

  [STEP](state, step) {
    state.step = step;
  },
  [SET_CANCELLATION_REASON](state, reason) {
    state.reason = reason;
  },

  [UPDATE_REFUND_INFO](state, payload) {
    Object.assign(state.refund, payload);
  },

  [UPDATE_RESCHEDULE_INFO](state, payload) {
    Object.assign(state.reschedule, payload);
  },

  [SET_TICKET_CREATED](state) {
    state.ticketCreated = true;
  },
  [SET_TICKET_CREATION_ERROR](state) {
    state.ticketCreationError = true;
  }
};

const getters = {
  isCancellation: state => {
    return state.cancellationReason === "cancellation";
  },

  isShortNotice: state => {
    return (
      moment
        .duration(moment(state.order.schedule_event.starts_at).diff(moment()))
        .asHours() >= 3 &&
      moment
        .duration(moment(state.order.schedule_event.starts_at).diff(moment()))
        .asHours() <= 24
    );
  },

  isCancellationPossible: state => {
    if (!state.order) return true;

    if (state.order.status === "service_not_provided") return true;

    const precedenceHours = moment
      .duration(moment(state.order.schedule_event.starts_at).diff(moment()))
      .asHours();

    if (precedenceHours < 0) {
      return true;
    }

    return precedenceHours > 3;
  },

  isPaidWithBoleto: state => {
    return orderMixin.methods.paidWithBoleto(state.order);
  },

  canFinishCancellation: (state, getters) => {
    if (state.step === 0 && state.cancellationAction === "inform-absence") {
      return true;
    }

    if (state.step === 1) {
      if (state.cancellationAction === "credit") {
        return true;
      }

      if (state.cancellationAction === "cancel" && !getters.isPaidWithBoleto) {
        return true;
      }
    }

    if (state.step === 2) {
      return true;
    }

    return false;
  },

  shouldRequestRefundInfo: (state, getters) => {
    return (
      state.step === 2 &&
      state.cancellationAction === "cancel" &&
      getters.isPaidWithBoleto
    );
  },

  shouldRequestRescheduleInfo: state => {
    return state.step === 2 && state.cancellationAction === "reschedule";
  },

  allowToSendTicket: state => {
    if (state.preloader) {
      return false;
    }

    if (state.step === 2 && state.cancellationAction === "cancel") {
      return (
        state.refund.bank &&
        state.refund.agency &&
        state.refund.account &&
        state.refund.accountType
      );
    }

    if (state.step === 2 && state.cancellationAction === "reschedule") {
      return state.reschedule.date && state.reschedule.initialHour;
    }

    return true;
  }
};

export default {
  namespaced: true,
  state: defaultState(),
  actions: actions,
  mutations: mutations,
  getters: getters
};



// WEBPACK FOOTER //
// ./src/store/modules/orders/orderCancellation.js